import type { DriverProfile } from "@/lib/drivers/types"
import { SWRConfiguration } from "swr"
import { LRUCache } from "lru-cache"
import { env } from "@/config"

const WORKER_URL =
  process.env.NEXT_PUBLIC_WORKER_URL || "https://odin-kv.ai-a8c.workers.dev"
let hasLoggedWorkerUrl = false

// Cache keys
export const CACHE_KEYS = {
  DRIVER_PROFILE: (id: string) => `driver:${id}`,
}

// SWR Configuration
export const SWR_CONFIG: SWRConfiguration = {
  revalidateOnFocus: false,
  revalidateOnReconnect: false,
  dedupingInterval: 86400000, // 24 hours
}

// Cache configuration
const CACHE_CONFIG = {
  MEMORY_TTL: 86400 * 1000, // 24 hours
  MAX_ITEMS: 1000, // Maximum number of profiles to cache
}

// Memory cache for driver profiles
const profileCache = new LRUCache<string, DriverProfile>({
  max: CACHE_CONFIG.MAX_ITEMS,
  ttl: CACHE_CONFIG.MEMORY_TTL,
})

// Constants for data limits
const MAX_REVIEWS = 10
const MAX_PHOTOS = 6
const MAX_TRIPS = 10

// Helper function to sanitize profile data
export const sanitizeProfileData = (profile: DriverProfile): DriverProfile => {
  const processedProfile = { ...profile }

  // Limit and sort reviews by most recent
  if (processedProfile.data.reviews?.length) {
    processedProfile.data.reviews = processedProfile.data.reviews
      .sort((a, b) => b.shipmentId - a.shipmentId)
      .slice(0, MAX_REVIEWS)
  }

  // Limit and sort photos by most recent
  if (processedProfile.data.userPhotos?.length) {
    processedProfile.data.userPhotos = processedProfile.data.userPhotos
      .sort((a, b) => Number(b.id) - Number(a.id))
      .slice(0, MAX_PHOTOS)
  }

  // Limit recent trips
  if (processedProfile.data.recentTrips?.length) {
    processedProfile.data.recentTrips = processedProfile.data.recentTrips.slice(
      0,
      MAX_TRIPS,
    )
  }

  return processedProfile
}

// Helper function to get a single driver profile
export async function getDriverProfile(
  id: string,
): Promise<DriverProfile | null> {
  // Check memory cache first
  const cached = profileCache.get(id)
  if (cached) {
    return cached
  }

  try {
    // Fetch from worker
    const response = await fetch(`${WORKER_URL}?id=${id}`)
    if (!response.ok) {
      // If worker fails, try direct API
      const apiResponse = await fetch(
        `${env("DAEDALUS_API_HOST")}/api/drivers/profile/${id}`,
      )
      if (!apiResponse.ok) return null
      const apiProfile = (await apiResponse.json()) as DriverProfile
      const sanitizedProfile = sanitizeProfileData(apiProfile)
      profileCache.set(id, sanitizedProfile)
      return sanitizedProfile
    }

    const profile = (await response.json()) as DriverProfile
    if (!profile?.data?.id) return null

    const sanitizedProfile = sanitizeProfileData(profile)
    profileCache.set(id, sanitizedProfile)
    return sanitizedProfile
  } catch (error) {
    return null
  }
}

// Export types for use in components
export type { DriverProfile }
